import { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { soundchartsEndpoints } from "../../repositories/soundcharts";
import { formatDisplayDate } from "../../DashboardPage/utils/dates";
import Swal from "sweetalert2";
import Button from "../ui/Button";

interface RadioSpinsExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  uuid: string | null;
  defaultCountry?: string;
  artistName?: string;
  releaseTitle?: string;
}

const COUNTRIES = [
  { code: "US", label: "United States" },
  { code: "GB", label: "United Kingdom" },
];

const MAX_PAGE_SIZE = 100; // Soundcharts API page size limit per request
const MAX_ROW_SPAN = 1000; // Max rows exportable at once (rowEnd - rowStart + 1)
const DEFAULT_ROW_START = 1;
const DEFAULT_ROW_END = 100;

const RadioSpinsExportModal = ({
  isOpen,
  onClose,
  uuid,
  defaultCountry,
  artistName,
  releaseTitle,
}: RadioSpinsExportModalProps) => {
  const [selectedCountries, setSelectedCountries] = useState<string[]>(
    defaultCountry ? [defaultCountry] : ["US"]
  );
  const [rowStart, setRowStart] = useState(DEFAULT_ROW_START);
  const [rowEnd, setRowEnd] = useState(DEFAULT_ROW_END);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedCountries(defaultCountry ? [defaultCountry] : ["US"]);
      setRowStart(DEFAULT_ROW_START);
      setRowEnd(DEFAULT_ROW_END);
    }
  }, [isOpen, defaultCountry]);

  const toggleCountry = (code: string) => {
    setSelectedCountries((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  // Fetches rows [offset, offset + count) for a country, paginating in
  // MAX_PAGE_SIZE-sized requests since the API caps limit per call.
  const fetchSpinsForCountry = async (country: string, offset: number, count: number) => {
    const allItems: any[] = [];
    let remaining = count;
    let currentOffset = offset;

    while (remaining > 0) {
      const pageLimit = Math.min(MAX_PAGE_SIZE, remaining);
      const query = `offset=${currentOffset}&limit=${pageLimit}&countryCode=${country}`;
      const response = await soundchartsEndpoints.getRadioSpins(uuid as string, query);
      const items = response?.data?.items || [];
      allItems.push(...items.map((item: any) => ({ ...item, _country: country })));

      if (items.length < pageLimit) break; // no more data available

      currentOffset += pageLimit;
      remaining -= pageLimit;
    }

    return allItems;
  };

  const handleExport = async () => {
    if (!uuid || selectedCountries.length === 0) return;

    const offset = rowStart - 1;
    const count = rowEnd - rowStart + 1;
    if (offset < 0 || count <= 0) return;
    if (count > MAX_ROW_SPAN) return;

    try {
      setIsLoading(true);
      const results = await Promise.all(
        selectedCountries.map((c) => fetchSpinsForCountry(c, offset, count))
      );
      const allSpins = results.flat();

      if (allSpins.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "No Data Found",
          html: "No radio spins match your selected filters.<br><br>Please adjust your filters and try again.",
          position: "top-right",
        });
        return;
      }

      const today = new Date().toISOString().slice(0, 10);
      const safeArtist = artistName ? artistName.replace(/[^a-zA-Z0-9]/g, "") : "Artist";
      const safeRelease = releaseTitle ? releaseTitle.replace(/[^a-zA-Z0-9]/g, "") : "Release";
      const fileName = `RadioSpins_${safeArtist}_${safeRelease}_${today}.csv`;

      const csvHeader = ["Radio Station", "City", "Country", "Date"].join(",") + "\n";
      const csvRows = allSpins
        .map((spin) =>
          [
            spin.radio?.name || "Unknown Station",
            spin.radio?.cityName || "",
            spin.radio?.countryName || spin.radio?.countryCode || "",
            spin.airedAt ? formatDisplayDate(spin.airedAt) : "",
          ]
            .map((val) => `"${String(val).replace(/"/g, '""')}"`)
            .join(",")
        )
        .join("\n");

      const csvContent = csvHeader + csvRows;
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      Swal.fire({
        icon: "success",
        title: "Export Successful",
        text: "CSV file has been downloaded.",
        position: "center",
        timer: 2500,
        showConfirmButton: false,
      });
      onClose();
    } catch (error) {
      console.error("Radio spins export error:", error);
      Swal.fire({
        icon: "error",
        title: "Export Failed",
        position: "top-end",
        text: "Export failed. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const rowSpan = rowEnd - rowStart + 1;
  const exceedsMaxSpan = rowSpan > MAX_ROW_SPAN;
  const canExport =
    selectedCountries.length > 0 &&
    !!uuid &&
    rowStart >= 1 &&
    rowEnd >= rowStart &&
    !exceedsMaxSpan;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-md p-6 bg-white rounded-lg shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-black">Export Radio Spins</h2>
          <button
            onClick={onClose}
            className="p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 border border-transparent"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <h3 className="mb-3 text-sm font-semibold text-neutral-700">Countries</h3>
            <div className="flex flex-col gap-3">
              {COUNTRIES.map((country) => (
                <label key={country.code} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedCountries.includes(country.code)}
                    onChange={() => toggleCountry(country.code)}
                    className="text-primary-700 form-checkbox"
                  />
                  <span>{country.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold text-neutral-700">Row Range</h3>
            <div className="flex items-center gap-3">
              <label className="flex flex-col text-xs text-neutral-600">
                From
                <input
                  type="number"
                  min={1}
                  value={rowStart === 0 ? "" : rowStart}
                  onChange={(e) =>
                    setRowStart(e.target.value === "" ? 0 : Number(e.target.value))
                  }
                  className="w-20 px-2 py-1 mt-1 text-sm border border-neutral-300 rounded focus:outline-none focus:border-primary-500"
                />
              </label>
              <label className="flex flex-col text-xs text-neutral-600">
                To
                <input
                  type="number"
                  min={rowStart}
                  value={rowEnd === 0 ? "" : rowEnd}
                  onChange={(e) =>
                    setRowEnd(e.target.value === "" ? 0 : Number(e.target.value))
                  }
                  className="w-20 px-2 py-1 mt-1 text-sm border border-neutral-300 rounded focus:outline-none focus:border-primary-500"
                />
              </label>
            </div>
            {exceedsMaxSpan && (
              <p className="mt-2 text-xs text-danger-500">
                You can only export up to {MAX_ROW_SPAN} rows at a time. Please narrow the row range.
              </p>
            )}
            {!exceedsMaxSpan && rowEnd < rowStart && (
              <p className="mt-2 text-xs text-danger-500">
                &quot;To&quot; must be greater than or equal to &quot;From&quot;.
              </p>
            )}
          </div>

          {rowEnd >= rowStart && !exceedsMaxSpan && (
            <p className="text-xs text-neutral-500">
              Exports radio spins ranked {rowStart} to {rowEnd} (most recent first) per selected country.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant="neutral"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button onClick={handleExport} loading={isLoading} disabled={!canExport}>
            {isLoading ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RadioSpinsExportModal;
